<!-- 通用按钮组件，替换van-button -->
<template>
  <button
    :class="buttonClass"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  type?: 'primary' | 'secondary' | 'danger' | 'default'
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  size: 'medium',
  disabled: false
})

defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClass = computed(() => {
  // 基础样式
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'border-none',
    'rounded-lg',
    'font-medium',
    'cursor-pointer',
    'transition-all',
    'duration-200',
    'ease-linear',
    'gap-1',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-blue-200'
  ]

  // 尺寸样式
  const sizeClasses = {
    small: ['px-3', 'py-1.5', 'text-xs', 'leading-tight'],
    medium: ['px-4', 'py-2', 'text-sm', 'leading-normal'],
    large: ['px-6', 'py-3', 'text-base', 'leading-normal']
  }

  // 类型样式
  const typeClasses = {
    primary: [
      'bg-gradient-to-br',
      'from-blue-500',
      'to-blue-600',
      'text-white',
      'shadow-lg',
      'shadow-blue-500/25'
    ],
    secondary: ['bg-slate-50', 'text-slate-500', 'border', 'border-slate-200'],
    danger: [
      'bg-gradient-to-br',
      'from-red-500',
      'to-red-600',
      'text-white',
      'shadow-lg',
      'shadow-red-500/25'
    ],
    default: ['bg-white', 'text-gray-700', 'border', 'border-gray-300']
  }

  // 禁用状态
  const disabledClasses = props.disabled
    ? ['opacity-50', 'cursor-not-allowed']
    : []

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...typeClasses[props.type],
    ...disabledClasses
  ].join(' ')
})
</script>

<style scoped></style>
